import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "@/core/services/store";
import { oAuthConfig } from "@/components/ExternalApps/SalesChannelManagementApp/utility/oAuthConfig";
import {
  amazonDefaultRegion,
  apiEndpoints
} from "@/components/ExternalApps/SalesChannelManagementApp/utility/amazonRegions";
import i18n from "@/core/plugins/vue-i18n";
import { updateOauthEnvironment } from "@/composables/useSalesChannelManagementApp";
import { labelData } from "@/components/ExternalApps/SalesChannelManagementApp/utility/labelData";
import { getSubdomain } from "@/core/services/api.service";
import { baseUrl as bexioBaseUrl } from "@/components/ExternalApps/SalesChannelManagementApp/config/apps/bexioApp";

export default function useOauth(integration) {
  const store = useStore();
  const value = ref({});
  const authSuccess = ref(false);
  const wasAlreadyInstalled = !!store.getters.integrations.length;

  const formConfig = {
    enableVariables: false,
    labelStacked: true,
    snippetPrefix: "salesChannelManagementApp",
    ...labelData.value
  };

  const selectedAppName = computed(() => store.getters.selectedApp.name);
  const selectedAppLabel = computed(() => store.getters.selectedApp.label);
  const canToggleSandBox = computed(
    () =>
      store.getters.selectedApp?.environmentVariables?.oauth
        ?.canToggleSandbox ?? false
  );

  const xentralConfig = store.getters.xentralConfig?.data;
  const fromUrl =
    xentralConfig?.baseUrl?.replace(/\/$/, "") || window.location.origin;
  const endpoint =
    xentralConfig?.xentralAppUri ||
    window.location.pathname.replace(/^\//, "") + window.location.hash;

  // The universal OAuth callback (oauth2/authorize/{thirdParty}) in the core just
  // forwards to the authorize endpoint of the target system.
  const redirectBaseUrl =
    {
      production: process.env?.VUE_APP_OAUTH_PRODUCTION_BASE_URL,
      staging: process.env?.VUE_APP_OAUTH_STAGING_BASE_URL,
      local: process.env?.VUE_APP_OAUTH_LOCAL_BASE_URL
    }[process.env?.VUE_APP_ENVIRONMENT] || "";

  const fullFromUrl =
    fromUrl + (endpoint.startsWith("/") ? "" : "/") + endpoint;
  const stateObj = computed(() => {
    return {
      // Use the store licenseId as before; only locally (store empty) fall back
      // to the URL subdomain, e.g. demo.localhost -> "demo" (CONNECT-6119).
      licenseId:
        store.getters.licenseId || getSubdomain(window.location.origin),
      selectedAppName: selectedAppName.value,
      from_url: fullFromUrl,
      redirectBaseUrl: redirectBaseUrl,
      integration: {
        ...integration,
        credentials: { ...value.value, ...handleAppSpecificConfiguration() }
      }
    };
  });

  const module = computed(() => {
    let tempModule = oAuthConfig?.find(
      system => system.name === selectedAppName.value
    );
    tempModule.config.form.disabled = authSuccess.value;

    return tempModule;
  });

  const dynamicFieldNames = ["helpTextLabel"];

  const form = computed(() => {
    const baseForm = module.value?.config?.form ?? [];
    const result = baseForm.filter(f => !dynamicFieldNames.includes(f.name));

    const helpTextLabel = i18n.te(
      `salesChannelManagementApp.${selectedAppName.value}.helpText`
    );
    if (helpTextLabel) {
      result.push({
        name: "helpTextLabel",
        label: i18n.t(
          `salesChannelManagementApp.${selectedAppName.value}.helpText`
        ),
        type: "label"
      });
    }

    const label = i18n.t(
      `salesChannelManagementApp.${selectedAppName.value}.useSandbox`,
      { appName: selectedAppLabel.value }
    );
    // if third party app allows switching between sandbox and production => inject switch
    // Only show in staging environment
    if (
      canToggleSandBox.value &&
      !baseForm.length &&
      process.env?.VUE_APP_ENVIRONMENT === "staging"
    ) {
      result.push({
        name: "toggleSandbox",
        infoText: label,
        type: "checkbox",
        default: true,
        disabled: true
      });
    }

    return result;
  });

  // Display a permanent installation link as first step
  const ottoInvitationLinkButton = computed(() =>
    selectedAppName.value === "otto" && !value.value?.toggleSandbox
      ? process.env.VUE_APP_OTTO_APP_INSTALLATION_URL
      : ""
  );

  const params = computed(() => {
    const computedParams = {};
    for (const param of module.value.config.params) {
      if (param === "client_id" && !value.value?.toggleSandbox) {
        computedParams[param] = store.getters.oAuthClientId;
        continue;
      }
      computedParams[param] = store.getters.oauthVariable(param);
    }
    return computedParams;
  });

  function handleAppSpecificConfiguration() {
    if (selectedAppName.value === "amazon") {
      let region = value.value?.region;

      return {
        region: region,
        [region]: value.value?.[region]
      };
    }
  }

  /**
   * @param state
   * @param baseUrl
   * @param params (key: value)
   * @returns {string}
   */
  function generateAuthUrl(state, baseUrl, params) {
    params["state"] = utf8ToBase64(stateObj.value);

    params = !baseUrl.includes("oauth2/authorize/")
      ? new URLSearchParams(params)
      : new URLSearchParams({ state: params["state"] });

    return `${baseUrl}?${params}`;
  }

  const isStaging = computed(() => value.value?.toggleSandbox ?? false);

  const baseUrls = computed(() => ({
    amazon:
      apiEndpoints.find(region => region.value === value?.value?.region)?.url ??
      amazonDefaultRegion.value,
    shopify: "https://admin.shopify.com/admin/oauth/authorize",
    otto:
      "https://" +
      (isStaging.value ? "sandbox." : "") +
      "api.otto.market/oauth2/auth",
    tiktok: "https://services.tiktokshop.com/open/authorize",
    exact: "https://start.exactonline.nl/api/oauth2/auth",
    "mollie-pos": "https://my.mollie.com/oauth2/authorize",
    ebay: "https://auth.ebay.com/oauth2/authorize",
    bexio: bexioBaseUrl,
    etsy: `${redirectBaseUrl}/oauth2/authorize/etsy`,
    woltdelivery:
      process.env?.VUE_APP_ENVIRONMENT === "production"
        ? "https://developer.wolt.com/integrate"
        : "https://developer.development.dev.woltapi.com/integrate"
  }));

  const baseUrl = computed(() => baseUrls.value?.[selectedAppName.value]);

  const authorizationUrl = computed(() => {
    return generateAuthUrl(stateObj, baseUrl.value, params.value);
  });

  watch(
    () => value.value?.toggleSandbox,
    () => {
      // Determine the correct environment suffix
      const envSuffix = value.value?.toggleSandbox ? "_SANDBOX" : "";
      const upperAppName = selectedAppName.value.toUpperCase();

      // Construct client_id and redirect_uri
      const clientId =
        process.env[`VUE_APP_${upperAppName}_APP_CLIENT_ID${envSuffix}`];
      const redirectUri =
        process.env[
          `VUE_APP_${upperAppName}_APP_APPLICATION_REDIRECT_URL${envSuffix}`
        ];

      // Update OAuth environment
      updateOauthEnvironment({
        appName: selectedAppName.value,
        newClientId: clientId,
        redirectUri: redirectUri
      });
    }
  );

  function utf8ToBase64(obj) {
    const json = JSON.stringify(obj);
    const utf8Bytes = new TextEncoder().encode(json);
    const binaryString = String.fromCharCode(...utf8Bytes);

    return btoa(binaryString);
  }

  /**
   * Builds the core URL that renews the token of an existing integration. The core
   * builds the consent URL from the stored connection and redirects back to `target`
   * as-is, so the target carries the state that re-opens the integration's settings
   */
  function reauthorizationUrl(integrationId, target) {
    const returnState = encodeURIComponent(
      utf8ToBase64({ fetched: true, project_id: integrationId })
    );
    const separator = target.includes("?") ? "&" : "?";
    const fromUrl = utf8ToBase64(`${target}${separator}state=${returnState}`);

    return (
      `https://oauth.api.${process.env.VUE_APP_CORE_DOMAIN}/apps/initializeReauthorization/${integrationId}` +
      `?from_url=${encodeURIComponent(fromUrl)}`
    );
  }

  const base64ToUtf8 = base64UrlEncoded => {
    if (!base64UrlEncoded) return;
    // decode  url encoded characters
    const base64 = decodeURIComponent(base64UrlEncoded?.replaceAll("%3D", "="));

    // decode the base64 string
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    return JSON.parse(json);
  };

  onMounted(() => {
    if (window.location?.href?.split("config=").length) {
      authSuccess.value = integration?.credentials?.success;
    }

    // Initialize toggleSandbox to true in staging environment
    if (
      process.env?.VUE_APP_ENVIRONMENT === "staging" &&
      canToggleSandBox.value
    ) {
      value.value = { ...value.value, toggleSandbox: true };
    }
  });

  return {
    authorizationUrl,
    ottoInvitationLinkButton,
    wasAlreadyInstalled,
    value,
    authSuccess,
    formConfig,
    form,
    stateObj,
    params,
    utf8ToBase64,
    reauthorizationUrl,
    selectedAppName,
    base64ToUtf8
  };
}
